import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from '@/store/store';

export type DashboardView = 'dashboard' | 'sentiment' | 'trends';
export type AggregateType = 'average' | 'median' | 'std_dev';

interface CultureHubAnalyticsState {
  dashboardView: DashboardView;
  selectedPillar: string;
  selectedAggregate: AggregateType;
  selectedPerformanceMetrics: number[];
}

const initialState: CultureHubAnalyticsState = {
  dashboardView: 'dashboard',
  selectedPillar: '',
  selectedAggregate: 'average',
  selectedPerformanceMetrics: [],
};

const cultureHubAnalyticsSlice = createSlice({
  name: 'cultureHubAnalytics',
  initialState,
  reducers: {
    setDashboardView(state, action: PayloadAction<DashboardView>) {
      state.dashboardView = action.payload;
    },
    setSelectedPillar(state, action: PayloadAction<string>) {
      state.selectedPillar = action.payload;
    },
    setSelectedAggregate(state, action: PayloadAction<AggregateType>) {
      state.selectedAggregate = action.payload;
    },
    togglePerformanceMetric(state, action: PayloadAction<number>) {
      const metricId = action.payload;
      const index = state.selectedPerformanceMetrics.indexOf(metricId);
      if (index >= 0) {
        state.selectedPerformanceMetrics.splice(index, 1);
      } else {
        state.selectedPerformanceMetrics.push(metricId);
      }
    },
    resetAnalytics() {
      return initialState;
    },
  },
});

export const {
  setDashboardView,
  setSelectedPillar,
  setSelectedAggregate,
  togglePerformanceMetric,
  resetAnalytics,
} = cultureHubAnalyticsSlice.actions;

// Base selectors
const selectAnalyticsState = (state: RootState) => state.cultureHubAnalytics;

// Memoized selectors with reselect
export const getDashboardView = createSelector(
  [selectAnalyticsState],
  (state): DashboardView => state.dashboardView
);

export const getAnalyticsSelectedPillar = createSelector(
  [selectAnalyticsState],
  (state): string => state.selectedPillar
);

export const getSelectedAggregate = createSelector(
  [selectAnalyticsState],
  (state): AggregateType => state.selectedAggregate
);

export const getSelectedPerformanceMetrics = createSelector(
  [selectAnalyticsState],
  (state): number[] => state.selectedPerformanceMetrics
);

export default cultureHubAnalyticsSlice;
